import {appConfig} from "config/defaultConfig";
import {t} from "i18next";
import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest
} from "redux-saga/effects";
import {
  setErrorNotification,
  setSuccessNotification
} from "store/features/notificationsSlice";
import {selectInvestigationStatus} from "store/selectors/investigationSelectors";
import {formatGraphData} from "utils/graph/graphFormatting";
import {
  createNewInvestigation,
  createSavedInvestigation,
  fetchInvestigationEvents,
  fetchInvestigationGraph,
  fetchInvestigationStatus,
  fetchSavedSnapshot,
  inspectInvestigationQuery
} from "../../api/investigate";
import {
  clearInvestigation,
  createInvestigation,
  createInvestigationFailure,
  createInvestigationSuccess,
  getInvestigationEvents,
  getInvestigationEventsFailure,
  getInvestigationEventsSuccess,
  getInvestigationGraph,
  getInvestigationGraphFailure,
  getInvestigationGraphSuccess,
  getInvestigationStatus,
  getInvestigationStatusFailure,
  getInvestigationStatusSuccess,
  getSavedInvestigation,
  getSavedInvestigationFailure,
  getSavedInvestigationSuccess,
  getSnapshot,
  getSnapshotFailure,
  getSnapshotSuccess,
  investigationComplete,
  refreshInvestigation,
  saveInvestigation,
  saveInvestigationFailure,
  saveInvestigationSuccess,
  startInvestigationLoading
} from "../features/investigationSlice";

const {ROUTES} = appConfig.APP;

const STATUS_INTERVAL = 500;
const INITIAL_DATA_FETCH_TIMEOUT = 5000;
const CONFIRM_COMPLETE_RETRIES = 5;

export function* createInvestigationSaga(action) {
  try {
    const {query, navigate} = action.payload;

    const inspectedObservables = yield call(inspectInvestigationQuery, query);

    if (inspectInvestigationQuery?.length) {
      const {investigation_id} = yield call(
        createNewInvestigation,
        inspectedObservables
      );

      navigate(`${ROUTES.INVESTIGATE}/${investigation_id}`);
      yield put(createInvestigationSuccess(investigation_id));
    } else {
      yield put(
        setErrorNotification(t("investigationDetail.messages.invalidId"))
      );
      yield put(createInvestigationFailure());
    }
  } catch (error) {
    console.error(error);
    yield put(
      setErrorNotification(t("investigationDetail.messages.createFailure"))
    );
    yield put(createInvestigationFailure(error));
  }
}

/**
 * Polls the Status API.
 *
 * Important to note:
 * - `pending` and `complete` will initially be '0' and can remain at 0 for several iterations.
 * - The values will rise & fluctuate as backend makes API calls when the investigation is run and finally the values settle until pending === 0 and complete > 0.
 * - In order to confirm the investigation is complete, we continue to poll until we have received 5 consecutive results of pending === 0. This protects two things:
 *   - For large investigations, pending may initially equal 0 for several iterations and then rise. Prevents this function from falsely marking the investigation complete on one `pending === 0` result.
 *   - pending can also reduce to 0 while running the investigation and then rise back up. Again, we look for 5 consecutive confirmations before cancelling the polling.
 * - `isComplete` is a boolean indicating that polling has finished.
 * - `pending` and `complete` are numerical values indicating the total number of API calls by the backend.
 */
export function* getInvestigationStatusSaga(action) {
  try {
    const {id} = action.payload;
    let refreshStatus = true;
    let initialDataLoaded = false;
    let isCompleteRetries = CONFIRM_COMPLETE_RETRIES;

    while (refreshStatus) {
      const statusResponse = yield call(fetchInvestigationStatus, id);
      yield put(getInvestigationStatusSuccess(statusResponse));
      const {pendingCount, verdictsCount, sightingCount, indicatorsCount} =
        yield select(selectInvestigationStatus);

      if (
        !initialDataLoaded &&
        (verdictsCount || sightingCount || indicatorsCount)
      ) {
        // Fetch graph data onlyonce, after we first determine some data to be ready for us to render.
        yield put(
          refreshInvestigation({
            id,
            delay: INITIAL_DATA_FETCH_TIMEOUT
          })
        );
        initialDataLoaded = true;
      }

      // If pending > 0, reset counter. Otherwise decrement by 1.
      isCompleteRetries =
        pendingCount > 0 ? CONFIRM_COMPLETE_RETRIES : isCompleteRetries - 1;

      // Continue to query status if:
      // 1. There are still pending results, or
      // 2. We have polled 5 consecutive results where pending === 0
      refreshStatus = pendingCount > 0 || isCompleteRetries > 0;

      yield delay(STATUS_INTERVAL);
    }

    yield put(investigationComplete());
  } catch (error) {
    console.error(error);
    yield put(
      setErrorNotification(t("investigationDetail.messages.fetchEventsFailure"))
    );
    yield put(getInvestigationStatusFailure());
  }
}

export function* getInvestigationGraphSaga(action) {
  try {
    const {id, wait} = action.payload;
    yield delay(wait);

    const response = yield call(fetchInvestigationGraph, id);
    const formattedGraphData = yield call(formatGraphData, response);
    yield put(getInvestigationGraphSuccess({id, ...formattedGraphData}));
  } catch (error) {
    console.error(error);
    yield put(
      setErrorNotification(t("investigationDetail.messages.fetchGraphFailure"))
    );
    yield put(getInvestigationGraphFailure());
  }
}

export function* getInvestigationEventsSaga(action) {
  try {
    const {id} = action.payload;
    const response = yield call(fetchInvestigationEvents, id);
    yield put(getInvestigationEventsSuccess(response));
  } catch (error) {
    console.error(error);
    yield put(
      setErrorNotification(t("investigationDetail.messages.fetchEventsFailure"))
    );
    yield put(getInvestigationEventsFailure());
  }
}

export function* refreshInvestigationSaga(action) {
  try {
    const {id, delay: delayTime} = action.payload;
    yield put(startInvestigationLoading());

    if (delayTime) {
      // Temp logic to delay calling of the refresh if 'delay' is provided
      // in the action. Intended nly for first data query after running an investigation.
      yield delay(delayTime);
    }

    yield all([
      put(getInvestigationGraph({id})),
      put(getInvestigationEvents({id}))
    ]);
  } catch (error) {
    console.error(error);
  }
}

export function* getSnapshotSaga(action) {
  try {
    const {
      id: ctiaInvestigationId,
      short_description,
      navigate
    } = action.payload;

    const {investigation_id} = yield call(
      fetchSavedSnapshot,
      ctiaInvestigationId
    );

    const routeTo = `${ROUTES.INVESTIGATE}/${investigation_id}${ROUTES.SAVED}`;

    // If 'navigate' was passed with the action, use to forward to the route.
    // Otherwise, open in new tab
    if (navigate) {
      navigate(routeTo);
    } else {
      window.open(routeTo, "_blank");
    }

    // @todo short_description should come from Summary API, when ready but this is a work around
    // to populate from Save Investigations in the interim.
    yield put(getSnapshotSuccess({investigation_id, short_description}));
  } catch (error) {
    console.log(error);
    yield put(
      setErrorNotification(
        t("investigationDetail.messages.fetchSnapshotFailed")
      )
    );
    yield put(getSnapshotFailure(error));
  }
}

function* getSavedInvestigationSaga(action) {
  try {
    const {id} = action.payload;
    yield call(refreshInvestigationSaga, action);
    // @todo Fetch saved investigation detail
    yield put(getSavedInvestigationSuccess({id}));
  } catch (error) {
    console.error(error);
    yield put(
      setErrorNotification(
        t("investigationDetail.messages.fetchSavedInvestigationFailed")
      )
    );
    yield put(getSavedInvestigationFailure(error));
  }
}

function* saveInvestigationSaga(action) {
  try {
    const {id, title, description} = action.payload;
    const response = yield call(createSavedInvestigation, id, {
      title,
      description
    });
    yield put(saveInvestigationSuccess(response));
    yield put(
      setSuccessNotification(
        t("investigationDetail.messages.saveInvestigationSuccess")
      )
    );
  } catch (error) {
    console.error(error);
    yield put(saveInvestigationFailure(error));
    yield put(
      setErrorNotification(
        t("investigationDetail.messages.saveInvestigationFailed")
      )
    );
  }
}

/**
 * If user navigated away from investigation page while polling is running,
 * this will cancel the status polling and allow the redux store to reset.
 */
function* investigationStatusWatcher() {
  let action;
  while ((action = yield take(getInvestigationStatus))) {
    const pollStatusTask = yield fork(getInvestigationStatusSaga, action); // Initiate polling saga
    yield take(clearInvestigation); // Wait for 'clearInvestigation'
    yield cancel(pollStatusTask); // Cancel polling task if necessary.
  }
}

export default function* investigationSagas() {
  yield all([
    yield takeLatest(createInvestigation, createInvestigationSaga),
    yield takeLatest(getInvestigationGraph, getInvestigationGraphSaga),
    yield takeLatest(getInvestigationEvents, getInvestigationEventsSaga),
    yield takeLatest(refreshInvestigation, refreshInvestigationSaga),
    yield takeLatest(getSnapshot, getSnapshotSaga),
    yield takeLatest(getSavedInvestigation, getSavedInvestigationSaga),
    yield takeLatest(saveInvestigation, saveInvestigationSaga),
    yield investigationStatusWatcher()
  ]);
}
